<template>
    <card-skeleton>
        <card-title :text="t('profile.completeProfileTitle')"></card-title>
        <v-card-text>
            <v-form ref="form" @submit.prevent="handleSubmit">
                <form-section :is-loading="isLoading">
                    <input-field
                        id="FirstName"
                        v-model="completeProfile.firstName"
                        class="pt-4"
                        :required="true"
                        :disabled="isSubmitting"
                        :label="t('register.firstName')"
                    />
                </form-section>
                <form-section :is-loading="isLoading">
                    <input-field
                        id="LastName"
                        v-model="completeProfile.lastName"
                        class="pt-4"
                        :required="true"
                        :disabled="isSubmitting"
                        :label="t('register.lastName')"
                    />
                </form-section>
                <form-section :is-loading="isLoading">
                    <input-field
                        id="email"
                        v-model="completeProfile.email"
                        :disabled="isSubmitting"
                        :pattern="FIELD_PATTERNS.EMAIL"
                        :pattern-error="t('validation.emailRequired')"
                        :required="true"
                        :label="t('register.email')"
                    />
                </form-section>
                <form-section :is-loading="isLoading">
                    <phone-field id="profile-phone" v-model="completeProfile.phone" :label="t('profile.phone')" :disabled="true" />
                </form-section>
                <form-section :is-loading="isLoading">
                    <checkbox-field
                        id="acceptTerms"
                        v-model="completeProfile.acceptTerms"
                        :disabled="isSubmitting"
                        :required="true"
                        :label="t('register.iAgreeToTerms')"
                    />
                </form-section>
                <form-section :is-loading="isLoading">
                    <v-card-actions class="d-flex justify-end ma-0 pa-0">
                        <card-button
                            type="submit"
                            class="flex-1-0-0"
                            size="large"
                            variant="flat"
                            :inactive-label="t('inquiry.sendMessage')"
                            :disabled="isSubmitting"
                        >
                            {{ t('profile.profileSave') }}
                        </card-button>
                    </v-card-actions>
                </form-section>
            </v-form>
        </v-card-text>
        <notification-bar
            v-model:show="notificationBarState.show"
            :color="notificationBarState.color"
            :icon="notificationBarState.icon"
            :border="notificationBarState.border"
            :message="notificationBarState.message"
        />
    </card-skeleton>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserStore } from '@geta/kolumbus-frontend/features/authentication';
import { useContainer } from '@/plugins/inversify';
import { IProfileApi, IProfileApiId } from '@/features/Profile/services/Interface/IProfileApi';
import CardSkeleton from '@/features/common/components/CardSkeleton.vue';
import CardTitle from '@/features/common/components/CardTitle.vue';
import { CompleteProfileDto, PhoneDto } from '@/types/webapi';
import { i18nRoute } from '@/i18n';
import { routeNames } from '@/router';
import router from '@/router';
import { DEFAULT_COUNTRY, FIELD_PATTERNS } from '@/features/common/components/form/constants';
import { CheckboxField, FormSection, InputField, NotificationBar, PhoneField } from '@/features/common/components/form';
import { useFormData, useFormHandling, useNotification } from '@/features/common/components/form/composables';
import CardButton from '@/features/common/components/CardButton.vue';

const { t } = useI18n();
const userStore = useUserStore();
const container = useContainer();
const profileApi = container.get<IProfileApi>(IProfileApiId);

const isLoading = ref(false);

const completeProfile = ref({
    firstName: '',
    lastName: '',
    email: '',
    phone: { countryCode: DEFAULT_COUNTRY.PHONE_CODE, number: '' } as PhoneDto,
    acceptTerms: false
});

const { notificationBarState, notificationSuccess, notificationError } = useNotification();
const { form, isSubmitting, validateForm, toggleIsSubmitting, isFormEmpty } = useFormHandling();
const { formData, resetFormData } = useFormData(completeProfile);

const saveData = async (data: CompleteProfileDto) => {
    try {
        await profileApi.completeProfile(data);
        notificationBarState.value = notificationSuccess(t('profile.profileSaved'));
        await router.push(i18nRoute({ name: routeNames.registerSuccess }));
        return true;
    } catch {
        notificationBarState.value = notificationError(t('register.registerError'));
        return false;
    }
};

const submitForm = async () => {
    try {
        toggleIsSubmitting(true);
        const result = await saveData(formData.value);
        result && resetFormData(form);
    } catch (error) {
        console.error(error);
    } finally {
        toggleIsSubmitting(false);
    }
};

const handleSubmit = async () => {
    if (isFormEmpty()) return;
    const { valid } = await validateForm();
    if (!valid) return;
    await submitForm();
};

onMounted(() => {
    completeProfile.value.email = userStore.claims.find(x => x.type.endsWith('identity/claims/emailaddress'))?.value ?? '';

    const phoneClaim = userStore.claims.find(x => x.type === 'name')?.value ?? '';
    if (phoneClaim && phoneClaim.indexOf('_') > -1) {
        completeProfile.value.phone = {
            countryCode: '+' + phoneClaim.split('_')[0],
            number: phoneClaim.split('_')[1]
        };
    }
});
</script>
